import { Activity, AlertTriangle, RadioTower } from 'lucide-react';
import { StatStrip } from '@/components/ui/stat-strip';

interface PropertyStatStripProps {
  meterCount: number;
  gatewayCount: number;
  // True when at least one gateway has reported within the last 24h
  // (`Date.now() - lastSeenAt < STALE_MS`). Drives the RadioTower icon
  // tone — green when at least one gateway is live, muted otherwise.
  hasOnlineGateway: boolean;
  activeAlarmCount: number;
  onAlarmClick?: () => void;
}

// Three-cell strip anchored between the property header and the tab row.
// Buildings + Units counts live on the property list cards now (where
// they help users pick a property) rather than on the portal stat
// strip (where they were noise + caused a five-column layout to wrap
// awkwardly on mobile).
//
// Yellow is reserved for acknowledged alerts elsewhere in the UI; an
// active count is a real-now problem, hence red.
export function PropertyStatStrip({
  meterCount,
  gatewayCount,
  hasOnlineGateway,
  activeAlarmCount,
  onAlarmClick,
}: PropertyStatStripProps) {
  return (
    <StatStrip
      cells={[
        {
          icon: <Activity className="w-4 h-4" />,
          label: 'Meters',
          shortLabel: 'Meters',
          value: meterCount,
          // Mute when no meters are assigned — matches the Active Alerts
          // tile's pattern. A property with zero meters reads visually
          // as "not yet set up" instead of as "ready / blue."
          tone: meterCount > 0 ? 'brand' : 'muted',
        },
        {
          icon: <RadioTower className="w-4 h-4" />,
          label: 'Gateways',
          shortLabel: 'Gateways',
          value: gatewayCount,
          // Green when a gateway has reported recently — matches the
          // wireless-icon convention used on gateway-detail-page.tsx
          // (24h `lastSeenAt` threshold). Anything else (no gateways,
          // or stale-only gateways) reads as muted/gray so the icon
          // never falsely advertises "live" without an online gateway.
          tone: hasOnlineGateway ? 'success' : 'muted',
        },
        {
          icon: <AlertTriangle className="w-4 h-4" />,
          label: 'Active Alerts',
          shortLabel: 'Alerts',
          value: activeAlarmCount,
          tone: activeAlarmCount > 0 ? 'error' : 'muted',
          onClick: activeAlarmCount > 0 ? onAlarmClick : undefined,
        },
      ]}
    />
  );
}
